import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Image as ImageIcon } from 'lucide-react';
import AdjustThumbnailModal from 'components/TextProjects/AdjustThumbnailModal/AdjustThumbnailModal';
import ProjectThumbnailCard from 'components/TextProjects/ProjectThumbnailCard/ProjectThumbnailCard';
import {
  detectMediaKind,
  formatBytes,
  isUnsupportedHeic,
  MAX_FILE_SIZE,
} from 'helpers/projectMediaUpload';
import {
  defaultCoverAspectForMedia,
  inferCoverAspectRatio,
  PROJECT_CARD_ASPECT_RATIO,
  PROJECT_CARD_ASPECT_SQUARE,
  PROJECT_CARD_PREVIEW_WIDTH,
  PROJECT_CARD_THUMB_2X_WIDTH,
} from 'helpers/projectCoverAspect';
import {
  fromNormalisedCrop,
  generateAutomaticCover,
  toNormalisedCrop,
} from 'helpers/projectCoverAutoCrop';
import {
  clearStoredProjectCoverCrop,
  getStoredProjectCoverCrop,
  setStoredProjectCoverCrop,
} from 'helpers/projectCoverCropStorage';
import {
  formatMediaTimestamp,
  generateAutomaticVideoCover,
} from 'helpers/projectCoverVideoFrame';
import {
  generateAudioCardDataUrl,
  getAudioFileRowWaveformHeights,
} from 'helpers/projectCoverAudioCard';
import {
  uploadProjectMedia,
  discardProjectMedia,
} from 'redux/modules/textProjects';
import { getThumbnailByWidth } from 'helpers/HelperFunctions';
import styles from './ProjectMediaPanel.module.scss';

// Prefer concrete types/extensions over image/* so iOS is less likely to offer HEIC.
const ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,video/*,audio/*,.mp4,.mov,.mp3,.wav,.m4a,.flac,.jpg,.jpeg,.png,.gif,.webp';

const HEIC_ERROR =
  'HEIC/HEIF photos are not supported. Please export or choose a JPEG or PNG.';

const UNSUPPORTED_FILE_ERROR =
  'Unsupported file type. Please choose an image, video, or audio file.';

const COVER_MEDIA_TYPES = ['image', 'video', 'audio'];

/**
 * Pick a progressive audio URL from project.stream.audio for HTML5 Audio.
 * Prefers higher bitrates and m4a/mp3 over ogg for broader browser support.
 *
 * @param {object|null|undefined} stream
 * @returns {string|null}
 */
function getProjectAudioStreamUrl(stream) {
  if (!stream || Array.isArray(stream) || !stream.audio) {
    return null;
  }

  const audio = stream.audio;
  const bitrates = Object.keys(audio).sort((a, b) => Number(b) - Number(a));
  const preferredFormats = ['m4a', 'mp4', 'mp3', 'ogg', 'wav'];

  for (let i = 0; i < bitrates.length; i += 1) {
    const formats = audio[bitrates[i]];
    if (!formats || typeof formats !== 'object') {
      continue;
    }

    for (let j = 0; j < preferredFormats.length; j += 1) {
      const url = formats[preferredFormats[j]];
      if (typeof url === 'string' && url) {
        return url;
      }
    }

    const formatKeys = Object.keys(formats);
    for (let k = 0; k < formatKeys.length; k += 1) {
      const format = formatKeys[k];
      const url = formats[format];
      if (
        format !== 'application/x-mpegurl' &&
        typeof url === 'string' &&
        url
      ) {
        return url;
      }
    }
  }

  return null;
}

/**
 * Optional media add/replace panel for project create & edit forms.
 * Includes the Thumbnail section (live card preview) once media exists.
 */
const ProjectMediaPanel = ({
  mode,
  project,
  disabled,
  processing,
  projectTitle,
  projectDescription,
  firstRole,
  onChange,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const autoGenIdRef = useRef(0);
  /** Bumps on each pick/clear so stale upload completions cannot overwrite state. */
  const uploadGenIdRef = useRef(0);
  const audioRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewUrl, setLocalPreviewUrl] = useState(null);
  const [mediaKind, setMediaKind] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  /** Custom crop from Adjust modal (pixel box + source image for API). */
  const [cover, setCover] = useState(null);
  /** Auto-generated preview + normalised crop for the current image. */
  const [autoThumb, setAutoThumb] = useState(null);
  const [generatingThumb, setGeneratingThumb] = useState(false);
  const [thumbnailSource, setThumbnailSource] = useState('auto');
  const [imageNaturalSize, setImageNaturalSize] = useState(null);
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(null);
  const [audioDurationSeconds, setAudioDurationSeconds] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [frameSeconds, setFrameSeconds] = useState(null);
  const [showCoverCropper, setShowCoverCropper] = useState(false);
  const [cropperInitialFile, setCropperInitialFile] = useState(null);
  const [cropperInitialSrc, setCropperInitialSrc] = useState(null);
  const [cropperInitialCrop, setCropperInitialCrop] = useState(null);
  /** Session crop aspect for audio cover art (square default; inferred on re-edit). */
  const [audioCoverAspectRatio, setAudioCoverAspectRatio] = useState(
    PROJECT_CARD_ASPECT_SQUARE
  );
  const [error, setError] = useState(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const [removeThumbnail, setRemoveThumbnail] = useState(false);

  const currentMediaType = project?.mediaType;
  const hasExistingMedia =
    currentMediaType && currentMediaType !== 'text' && !removeMedia;
  const effectiveMediaType =
    mediaKind || (hasExistingMedia ? currentMediaType : null);
  const hasMediaForThumbnail =
    COVER_MEDIA_TYPES.includes(effectiveMediaType) &&
    !removeMedia &&
    (Boolean(file) || Boolean(uploaded) || hasExistingMedia);
  // Prefer full original for re-cropping in the Adjust modal / file-row thumb.
  const cropSourceUrl =
    project?.thumbnails?.original ||
    project?.thumbnails?.original_optimised ||
    null;
  // File row shows the source media, not the portfolio crop.
  const existingFileThumbUrl =
    project?.thumbnails?.original_optimised ||
    project?.thumbnails?.original ||
    null;
  const pickerDisabled = disabled || uploading;
  const coverAspectRatio =
    effectiveMediaType === 'audio'
      ? audioCoverAspectRatio
      : PROJECT_CARD_ASPECT_RATIO;
  const allowCoverAspectChoice = effectiveMediaType === 'audio';
  // Custom audio cover with a square crop: form preview matches browse music
  // framing. Generated cards and image/video stay 16:9.
  // Honour API `generated === false` before the source-badge effect flips
  // thumbnailSource, so the first paint already picks square derivatives.
  const showSquareCoverPreview =
    effectiveMediaType === 'audio' &&
    audioCoverAspectRatio === PROJECT_CARD_ASPECT_SQUARE &&
    (thumbnailSource === 'custom' ||
      (Boolean(project?.thumbnails) &&
        project.thumbnails.generated === false &&
        !cover &&
        !removeThumbnail &&
        !file &&
        !uploaded));
  // Live card preview: wide derivatives for portfolio 16:9; square when the
  // user chose a square audio cover (browse / music-row framing).
  const existingCardPreviewUrl = (() => {
    const images = project?.thumbnails?.images;
    if (
      images &&
      typeof images === 'object' &&
      Object.keys(images).length > 0
    ) {
      try {
        const sized = getThumbnailByWidth(images, PROJECT_CARD_THUMB_2X_WIDTH, {
          square: showSquareCoverPreview,
        });
        if (sized) return sized;
      } catch (err) {
        // fall through to originals
      }
    }
    return existingFileThumbUrl;
  })();

  const showLiveFileRow =
    mode === 'edit' && hasExistingMedia && !file && !uploaded && !uploading;
  const showNewFileRow = Boolean(file || uploading || uploaded);
  const showFileRow = showNewFileRow || showLiveFileRow;
  const showDropzone = !showFileRow;
  const resolvedTitle = projectTitle || project?.title || '';
  const rowMediaKind = mediaKind || (showLiveFileRow ? currentMediaType : null);
  // Edit live row: play from processed stream when there is no local File yet.
  const streamAudioUrl =
    !file && currentMediaType === 'audio' && !removeMedia
      ? getProjectAudioStreamUrl(project?.stream)
      : null;
  const canPreviewAudio =
    (Boolean(file) && mediaKind === 'audio') || Boolean(streamAudioUrl);

  useEffect(() => {
    if (!file || mediaKind !== 'image') {
      setLocalPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, mediaKind]);

  // Inline audio play/pause in the file row (local File or existing stream).
  useEffect(() => {
    let objectUrl = null;
    let src = null;

    if (file && mediaKind === 'audio') {
      objectUrl = URL.createObjectURL(file);
      src = objectUrl;
    } else if (streamAudioUrl) {
      src = streamAudioUrl;
    }

    if (!src) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setAudioPlaying(false);
      return undefined;
    }

    const audio = new Audio(src);
    audioRef.current = audio;

    const onLoaded = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setAudioDurationSeconds(audio.duration);
      }
    };
    const onEnded = () => setAudioPlaying(false);

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (audioRef.current === audio) {
        audioRef.current = null;
      }
      setAudioPlaying(false);
    };
  }, [file, mediaKind, streamAudioUrl]);

  // Restore Custom crop badge when a previous crop is stored for this project.
  // Prefer stored mid-video frame timestamp from the API when editing.
  // Audio: honour API `generated` so custom cover art keeps the Custom crop badge.
  useEffect(() => {
    if (cover) {
      setThumbnailSource('custom');
      return;
    }

    if (project?.id && getStoredProjectCoverCrop(project.id)) {
      setThumbnailSource('custom');
      return;
    }

    if (
      effectiveMediaType === 'audio' &&
      project?.thumbnails &&
      project.thumbnails.generated === false
    ) {
      setThumbnailSource('custom');
      return;
    }

    setThumbnailSource('auto');
  }, [cover, project?.id, project?.thumbnails, effectiveMediaType]);

  // Audio: restore Square vs Widescreen from the saved crop (or keep session default).
  useEffect(() => {
    if (effectiveMediaType !== 'audio') {
      return;
    }

    if (cover?.width && cover?.height) {
      setAudioCoverAspectRatio(
        inferCoverAspectRatio(cover.width, cover.height)
      );
      return;
    }

    if (!project?.id) {
      return;
    }

    const stored = getStoredProjectCoverCrop(project.id);
    if (
      stored &&
      typeof stored.w === 'number' &&
      typeof stored.h === 'number' &&
      stored.naturalWidth &&
      stored.naturalHeight
    ) {
      setAudioCoverAspectRatio(
        inferCoverAspectRatio(
          stored.w * stored.naturalWidth,
          stored.h * stored.naturalHeight
        )
      );
    }
  }, [cover, effectiveMediaType, project?.id]);

  useEffect(() => {
    if (mediaKind === 'video' || effectiveMediaType === 'video') {
      const apiFrame = project?.thumbnails?.frame_seconds;
      if (
        (frameSeconds === null || frameSeconds === undefined) &&
        typeof apiFrame === 'number'
      ) {
        setFrameSeconds(apiFrame);
      }
      const streamDuration = project?.stream?.duration;
      if (
        (videoDurationSeconds === null || videoDurationSeconds === undefined) &&
        typeof streamDuration === 'number' &&
        streamDuration > 0
      ) {
        setVideoDurationSeconds(streamDuration);
        if (
          (frameSeconds === null || frameSeconds === undefined) &&
          (apiFrame === null || apiFrame === undefined)
        ) {
          setFrameSeconds(Math.floor(streamDuration * 0.5));
        }
      }
    }
  }, [
    mediaKind,
    effectiveMediaType,
    project?.thumbnails?.frame_seconds,
    project?.stream?.duration,
    frameSeconds,
    videoDurationSeconds,
  ]);

  // Existing audio project: duration from stream when editing without a new file.
  useEffect(() => {
    if (effectiveMediaType !== 'audio' || file) {
      return;
    }
    const streamDuration = project?.stream?.duration;
    if (
      (audioDurationSeconds === null || audioDurationSeconds === undefined) &&
      typeof streamDuration === 'number' &&
      streamDuration > 0
    ) {
      setAudioDurationSeconds(streamDuration);
    }
  }, [
    effectiveMediaType,
    file,
    project?.stream?.duration,
    audioDurationSeconds,
  ]);

  // Live generated-card preview for audio (title baked in; refreshes on rename).
  useEffect(() => {
    if (effectiveMediaType !== 'audio' || thumbnailSource !== 'auto' || cover) {
      return;
    }

    const genId = ++autoGenIdRef.current;
    setGeneratingThumb(true);

    const dataUrl = generateAudioCardDataUrl(resolvedTitle);
    if (genId !== autoGenIdRef.current) {
      return;
    }

    if (dataUrl) {
      setAutoThumb({
        previewUrl: dataUrl,
        naturalWidth: PROJECT_CARD_PREVIEW_WIDTH * 2,
        naturalHeight: Math.round((PROJECT_CARD_PREVIEW_WIDTH * 2) / (16 / 9)),
      });
    }
    setGeneratingThumb(false);
  }, [effectiveMediaType, thumbnailSource, cover, resolvedTitle]);

  // Drop stale generated-card previews once a custom cover is active so the
  // edit-form card does not keep showing the waveform over server thumbs.
  useEffect(() => {
    if (thumbnailSource === 'custom') {
      setAutoThumb(null);
    }
  }, [thumbnailSource]);

  const clearFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (replaceInputRef.current) {
      replaceInputRef.current.value = '';
    }
  };

  const emitChange = (overrides = {}) => {
    const next = {
      uploaded,
      cover,
      removeMedia,
      removeThumbnail,
      mediaKind,
      uploading,
      autoThumb,
      localPreviewUrl: previewUrl,
      ...overrides,
    };

    const resolvedPreview =
      overrides.previewUrl !== undefined
        ? overrides.previewUrl
        : next.cover?.croppedImage ||
          next.autoThumb?.previewUrl ||
          (next.mediaKind === 'image' && next.localPreviewUrl
            ? next.localPreviewUrl
            : null) ||
          null;

    onChange({
      file: next.uploaded?.s3_url || null,
      // Send the original File or image URL (like portfolio avatar/cover), not
      // the cropped data URL — base64 covers go through UploadLocalFilesJob and
      // blow up request size; the API crops from these coordinates.
      thumbnail: next.cover?.image || null,
      crop: next.cover
        ? {
            thumbnail: {
              x: next.cover.x,
              y: next.cover.y,
              w: next.cover.width,
              h: next.cover.height,
            },
          }
        : null,
      remove_media: next.removeMedia || false,
      remove_thumbnail: next.removeThumbnail || false,
      mediaKind: next.mediaKind,
      uploading: next.uploading,
      thumbnailSource: next.cover ? 'custom' : 'auto',
      // Client-only: used for the project page hero while derivatives process.
      previewUrl: resolvedPreview,
    });
  };

  const runAutomaticCover = async (source, aspectRatio) => {
    const genId = ++autoGenIdRef.current;
    setGeneratingThumb(true);
    setAutoThumb(null);

    try {
      const result = await generateAutomaticCover(source, aspectRatio);
      if (genId !== autoGenIdRef.current) {
        return;
      }
      if (!result) {
        setGeneratingThumb(false);
        return;
      }

      setAutoThumb(result);
      setImageNaturalSize({
        width: result.naturalWidth,
        height: result.naturalHeight,
      });
      setThumbnailSource('auto');
    } catch (err) {
      if (genId === autoGenIdRef.current) {
        setAutoThumb(null);
      }
    } finally {
      if (genId === autoGenIdRef.current) {
        setGeneratingThumb(false);
      }
    }
  };

  const runAutomaticVideoCover = async (source) => {
    const genId = ++autoGenIdRef.current;
    setGeneratingThumb(true);
    setAutoThumb(null);
    setFrameSeconds(null);

    try {
      const result = await generateAutomaticVideoCover(
        source,
        PROJECT_CARD_ASPECT_RATIO
      );
      if (genId !== autoGenIdRef.current) {
        return;
      }
      if (!result) {
        setGeneratingThumb(false);
        return;
      }

      setAutoThumb(result);
      setImageNaturalSize({
        width: result.naturalWidth,
        height: result.naturalHeight,
      });
      setVideoDurationSeconds(result.durationSeconds);
      setFrameSeconds(result.frameSeconds);
      setThumbnailSource('auto');
    } catch (err) {
      if (genId === autoGenIdRef.current) {
        setAutoThumb(null);
      }
    } finally {
      if (genId === autoGenIdRef.current) {
        setGeneratingThumb(false);
      }
    }
  };

  const resolveInitialCrop = () => {
    if (cover) {
      return {
        x: cover.x,
        y: cover.y,
        width: cover.width,
        height: cover.height,
      };
    }

    // Let AdjustThumbnailModal derive auto crop so pendingSource stays 'auto'.
    if (thumbnailSource === 'auto') {
      return null;
    }

    if (autoThumb?.pixel) {
      return autoThumb.pixel;
    }

    if (!project?.id) {
      return null;
    }

    const stored = getStoredProjectCoverCrop(project.id);
    if (!stored) {
      return null;
    }

    const naturalWidth =
      stored.naturalWidth || imageNaturalSize?.width || autoThumb?.naturalWidth;
    const naturalHeight =
      stored.naturalHeight ||
      imageNaturalSize?.height ||
      autoThumb?.naturalHeight;

    if (naturalWidth && naturalHeight) {
      return fromNormalisedCrop(stored, naturalWidth, naturalHeight);
    }

    return null;
  };

  const openCoverCropper = ({
    initialFile = null,
    initialSrc = null,
    initialCrop = null,
  } = {}) => {
    setCropperInitialFile(initialFile);
    setCropperInitialSrc(initialSrc);
    setCropperInitialCrop(initialCrop);
    setShowCoverCropper(true);
  };

  const closeCoverCropper = () => {
    setShowCoverCropper(false);
    setCropperInitialFile(null);
    setCropperInitialSrc(null);
    setCropperInitialCrop(null);
  };

  const openCoverCropperForCurrentMedia = () => {
    if (effectiveMediaType === 'image') {
      if (file) {
        openCoverCropper({
          initialFile: file,
          initialCrop: resolveInitialCrop(),
        });
        return;
      }

      if (cropSourceUrl) {
        openCoverCropper({
          initialSrc: cropSourceUrl,
          initialCrop: resolveInitialCrop(),
        });
        return;
      }
    }

    // Audio / video: pick a cover in the shared Adjust thumbnail modal.
    if (cover?.image instanceof File || cover?.image instanceof Blob) {
      openCoverCropper({
        initialFile: cover.image,
        initialCrop: resolveInitialCrop(),
      });
      return;
    }

    if (typeof cover?.image === 'string') {
      openCoverCropper({
        initialSrc: cover.image,
        initialCrop: resolveInitialCrop(),
      });
      return;
    }

    // Audio with existing custom cover (not generated): re-open from source.
    if (
      effectiveMediaType === 'audio' &&
      thumbnailSource === 'custom' &&
      cropSourceUrl &&
      project?.thumbnails?.generated === false
    ) {
      openCoverCropper({
        initialSrc: cropSourceUrl,
        initialCrop: resolveInitialCrop(),
      });
      return;
    }

    openCoverCropper();
  };

  const resetLocalMedia = () => {
    autoGenIdRef.current += 1;
    uploadGenIdRef.current += 1;
    setFile(null);
    setMediaKind(null);
    setProgress(0);
    setUploaded(null);
    setCover(null);
    setAutoThumb(null);
    setGeneratingThumb(false);
    setImageNaturalSize(null);
    setVideoDurationSeconds(null);
    setAudioDurationSeconds(null);
    setFrameSeconds(null);
    setThumbnailSource('auto');
    setAudioCoverAspectRatio(PROJECT_CARD_ASPECT_SQUARE);
    closeCoverCropper();
    setError(null);
    clearFileInput();
  };

  const handlePick = async (picked) => {
    if (!picked) return;

    // Allow re-selecting the same file after an error or clear (iOS Safari).
    clearFileInput();
    setError(null);

    if (picked.size > MAX_FILE_SIZE) {
      setError('File must be 10GB or smaller');
      return;
    }

    if (isUnsupportedHeic(picked)) {
      setError(HEIC_ERROR);
      return;
    }

    const kind = detectMediaKind(picked);
    if (kind === 'other') {
      setError(UNSUPPORTED_FILE_ERROR);
      return;
    }

    if (project?.id) {
      clearStoredProjectCoverCrop(project.id);
    }

    // Discard the previous completed queue object on replace (same idea as
    // handleClearNewMedia). Best-effort; do not block the new upload.
    const previousCompletedKey = uploaded?.key;
    if (previousCompletedKey) {
      dispatch(discardProjectMedia(previousCompletedKey)).catch(() => {});
    }

    const uploadGenId = ++uploadGenIdRef.current;

    setFile(picked);
    setMediaKind(kind);
    setUploaded(null);
    setCover(null);
    setAutoThumb(null);
    setImageNaturalSize(null);
    setVideoDurationSeconds(null);
    setAudioDurationSeconds(null);
    setFrameSeconds(null);
    setThumbnailSource('auto');
    setAudioCoverAspectRatio(defaultCoverAspectForMedia(kind));
    setRemoveMedia(false);
    setRemoveThumbnail(false);
    setUploading(true);
    setProgress(0);

    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: false,
      removeThumbnail: false,
      mediaKind: kind,
      uploading: true,
    });

    // Image / video: generate automatic card crop immediately (no modal).
    // Audio: generated card is painted in the title effect below.
    // Publish is never blocked on extraction finishing.
    if (kind === 'image') {
      runAutomaticCover(picked, PROJECT_CARD_ASPECT_RATIO);
    } else if (kind === 'video') {
      runAutomaticVideoCover(picked);
    } else {
      autoGenIdRef.current += 1;
      setGeneratingThumb(false);
    }

    try {
      const result = await dispatch(
        uploadProjectMedia(picked, (percent) => {
          if (uploadGenId === uploadGenIdRef.current) {
            setProgress(percent);
          }
        })
      );
      if (uploadGenId !== uploadGenIdRef.current) {
        // Superseded by a newer pick/clear — drop the orphaned queue object.
        if (result?.key) {
          dispatch(discardProjectMedia(result.key)).catch(() => {});
        }
        return;
      }
      setUploaded(result);
      setUploading(false);
      emitChange({
        uploaded: result,
        cover: null,
        removeMedia: false,
        removeThumbnail: false,
        mediaKind: kind,
        uploading: false,
      });
    } catch (err) {
      if (uploadGenId !== uploadGenIdRef.current) {
        return;
      }
      setUploading(false);
      setError(err?.message || 'Upload failed. Please try again.');
      emitChange({
        uploaded: null,
        cover: null,
        removeMedia: false,
        removeThumbnail: false,
        mediaKind: kind,
        uploading: false,
      });
    }
  };

  const handleCoverSave = (details) => {
    // `null` = Reset to automatic then Save — clear custom crop; API uses
    // autocrop (mid-video frame / generated audio card).
    if (!details) {
      if (project?.id) {
        clearStoredProjectCoverCrop(project.id);
      }
      setCover(null);
      setThumbnailSource('auto');

      // Edit + existing media: remove custom cover so the API regenerates
      // the automatic crop / mid-video frame / generated audio card.
      const shouldRemoveCustom =
        mode === 'edit' && hasExistingMedia && !uploaded;

      setRemoveThumbnail(shouldRemoveCustom);
      closeCoverCropper();
      emitChange({
        cover: null,
        removeMedia: false,
        removeThumbnail: shouldRemoveCustom,
        uploading: false,
      });

      // Re-run automatic generation so the preview refreshes.
      if (effectiveMediaType === 'image') {
        if (file) {
          runAutomaticCover(file, coverAspectRatio);
        } else if (cropSourceUrl) {
          runAutomaticCover(cropSourceUrl, coverAspectRatio);
        }
      } else if (effectiveMediaType === 'video' && file) {
        runAutomaticVideoCover(file);
      }
      // Audio: title effect regenerates the generated card preview.
      return;
    }

    setCover(details);
    setThumbnailSource('custom');
    setRemoveThumbnail(false);
    setAutoThumb(null);
    closeCoverCropper();

    if (details.width && details.height) {
      setAudioCoverAspectRatio(
        inferCoverAspectRatio(details.width, details.height)
      );
    } else if (
      typeof details.aspectRatio === 'number' &&
      details.aspectRatio > 0
    ) {
      setAudioCoverAspectRatio(
        details.aspectRatio === PROJECT_CARD_ASPECT_SQUARE ||
          details.aspectRatio === 1
          ? PROJECT_CARD_ASPECT_SQUARE
          : PROJECT_CARD_ASPECT_RATIO
      );
    }

    const replacedWithFile =
      details.image instanceof File || details.image instanceof Blob;

    // Replacement file: drop prior-image crop storage and natural size so we
    // never normalise against the old dimensions / URL.
    if (replacedWithFile && project?.id) {
      clearStoredProjectCoverCrop(project.id);
    }

    const naturalWidth = replacedWithFile
      ? details.naturalWidth || null
      : details.naturalWidth ||
        imageNaturalSize?.width ||
        autoThumb?.naturalWidth ||
        null;
    const naturalHeight = replacedWithFile
      ? details.naturalHeight || null
      : details.naturalHeight ||
        imageNaturalSize?.height ||
        autoThumb?.naturalHeight ||
        null;

    let normalised = null;
    if (naturalWidth && naturalHeight) {
      normalised = toNormalisedCrop(
        {
          x: details.x,
          y: details.y,
          width: details.width,
          height: details.height,
        },
        naturalWidth,
        naturalHeight
      );
      setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
    } else if (replacedWithFile) {
      setImageNaturalSize(null);
    }

    if (project?.id && normalised) {
      let imageUrl;
      if (typeof details.image === 'string') {
        imageUrl = details.image;
      } else if (!replacedWithFile) {
        imageUrl = cropSourceUrl || undefined;
      }

      setStoredProjectCoverCrop(project.id, {
        ...normalised,
        imageUrl,
        naturalWidth,
        naturalHeight,
      });
    }

    emitChange({
      cover: details,
      removeMedia: false,
      removeThumbnail: false,
      uploading: false,
    });
  };

  const handleResetToAutomatic = () => {
    handleCoverSave(null);
  };

  const handleRemoveMedia = () => {
    if (project?.id) {
      clearStoredProjectCoverCrop(project.id);
    }
    resetLocalMedia();
    setRemoveMedia(true);
    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: true,
      removeThumbnail: false,
      mediaKind: null,
      uploading: false,
    });
  };

  const handleClearNewMedia = async () => {
    const completedKey = uploaded?.key;

    if (completedKey) {
      try {
        await dispatch(discardProjectMedia(completedKey));
      } catch (err) {
        // best-effort cleanup of the completed queue object
      }
    }

    if (project?.id) {
      clearStoredProjectCoverCrop(project.id);
    }

    resetLocalMedia();
    emitChange({
      uploaded: null,
      cover: null,
      removeMedia: false,
      removeThumbnail: false,
      mediaKind: null,
      uploading: false,
    });
  };

  const toggleAudioPlayback = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio
        .play()
        .then(() => setAudioPlaying(true))
        .catch(() => setAudioPlaying(false));
    } else {
      audio.pause();
      setAudioPlaying(false);
    }
  };

  const previewImageUrl = (() => {
    if (cover?.croppedImage) {
      return cover.croppedImage;
    }
    // Removal in progress: keep generated card for form / interim publish
    // preview; only blank when no auto thumb exists yet.
    if (removeThumbnail) {
      return autoThumb?.previewUrl || null;
    }
    // Custom cover: never prefer a stale generated-card / auto crop preview.
    if (thumbnailSource === 'custom') {
      if (existingCardPreviewUrl && !uploaded) {
        return existingCardPreviewUrl;
      }
      if (mediaKind === 'image' && previewUrl) {
        return previewUrl;
      }
      return null;
    }
    if (
      effectiveMediaType === 'audio' &&
      thumbnailSource === 'auto' &&
      autoThumb?.previewUrl
    ) {
      // Prefer live generated card so title edits show immediately.
      return autoThumb.previewUrl;
    }
    if (autoThumb?.previewUrl) {
      return autoThumb.previewUrl;
    }
    if (existingCardPreviewUrl && !uploaded) {
      return existingCardPreviewUrl;
    }
    if (mediaKind === 'image' && previewUrl) {
      return previewUrl;
    }
    return null;
  })();

  // Keep parent payload previewUrl in sync for the post-publish project hero.
  useEffect(() => {
    emitChange({ previewUrl: previewImageUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when preview URL changes
  }, [previewImageUrl]);

  const showThumbSkeleton =
    generatingThumb ||
    (effectiveMediaType === 'image' &&
      Boolean(file) &&
      !cover?.croppedImage &&
      !autoThumb?.previewUrl &&
      !previewImageUrl) ||
    (effectiveMediaType === 'video' &&
      thumbnailSource === 'auto' &&
      !cover?.croppedImage &&
      !autoThumb?.previewUrl &&
      (file
        ? !previewImageUrl
        : Boolean(project?.thumbnails?.placeholder) || !previewImageUrl));

  const videoFrameLabel =
    effectiveMediaType === 'video' &&
    thumbnailSource === 'auto' &&
    typeof frameSeconds === 'number'
      ? `Frame at ${formatMediaTimestamp(frameSeconds)}`
      : null;

  const cardBadgeLabel =
    effectiveMediaType === 'audio' && thumbnailSource === 'auto'
      ? 'Generated card'
      : null;

  const fileMetaDuration =
    rowMediaKind === 'audio' && typeof audioDurationSeconds === 'number'
      ? ` · ${formatMediaTimestamp(audioDurationSeconds)}`
      : rowMediaKind === 'video' && typeof videoDurationSeconds === 'number'
      ? ` · ${formatMediaTimestamp(videoDurationSeconds)}`
      : '';

  const liveFileName = (() => {
    if (!showLiveFileRow) return null;
    if (currentMediaType === 'image') return 'Project image';
    if (currentMediaType === 'video') return 'Project video';
    if (currentMediaType === 'audio') return 'Project audio';
    return 'Project media';
  })();

  const liveFileMeta = (() => {
    if (!showLiveFileRow) return '';
    const parts = [];
    if (project?.size) {
      parts.push(formatBytes(project.size));
    }
    if (currentMediaType) {
      parts.push(currentMediaType);
    }
    if (fileMetaDuration) {
      parts.push(fileMetaDuration.replace(/^\s·\s/, ''));
    }
    parts.push('live');
    return parts.join(' · ');
  })();

  const renderThumb = () => {
    if (rowMediaKind === 'image') {
      const thumbSrc =
        (mediaKind === 'image' && previewUrl) ||
        (showLiveFileRow ? existingFileThumbUrl : null);
      if (thumbSrc) {
        return <img src={thumbSrc} alt="" className={styles.fileThumbImage} />;
      }
    }

    if (rowMediaKind === 'video') {
      return (
        <div className={styles.videoFileThumb} aria-hidden>
          <span className={styles.videoFilePlay} />
          {typeof videoDurationSeconds === 'number' ? (
            <span className={styles.videoFileDuration}>
              {formatMediaTimestamp(videoDurationSeconds)}
            </span>
          ) : null}
        </div>
      );
    }

    if (rowMediaKind === 'audio') {
      return (
        <div className={styles.audioFileThumb}>
          {canPreviewAudio ? (
            <button
              type="button"
              className={styles.audioPlayButton}
              onClick={toggleAudioPlayback}
              disabled={disabled || uploading}
              aria-label={audioPlaying ? 'Pause audio' : 'Play audio'}
            >
              {audioPlaying ? (
                <span className={styles.audioPauseIcon} aria-hidden />
              ) : (
                <span className={styles.audioPlayTriangle} aria-hidden />
              )}
            </button>
          ) : (
            <span className={styles.audioPlayButton} aria-hidden>
              <span className={styles.audioPlayTriangle} />
            </span>
          )}
          <div className={styles.audioWaveform} aria-hidden>
            {getAudioFileRowWaveformHeights().map((ratio, index) => (
              <span
                // Static decorative bars — index key is stable.
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={styles.audioWaveformBar}
                style={{ height: `${Math.round(ratio * 100)}%` }}
              />
            ))}
          </div>
        </div>
      );
    }

    return <ImageIcon size={28} strokeWidth={1.75} aria-hidden />;
  };

  const showAdjustResetActions =
    !(effectiveMediaType === 'video' && thumbnailSource === 'auto') &&
    !(effectiveMediaType === 'audio' && thumbnailSource === 'auto');

  return (
    <div className={styles.section}>
      <div className={styles.heading}>Project media</div>
      {mode === 'create' && (
        <p className={styles.help}>
          The lead file for this project — it shows at the top of the page. Or
          keep it text only.
        </p>
      )}

      {processing && (
        <div className={styles.processingBanner} role="status">
          Your media is still processing. Streams and thumbnails may not be
          ready yet. You can still edit the title, description, and roles.
          Replacing media will restart processing.
        </div>
      )}

      {removeMedia && (
        <p className={styles.help}>
          Media will be removed when you save — this project will become
          text-only.
        </p>
      )}

      {mode === 'edit' && !showLiveFileRow && !showNewFileRow && (
        <p className={styles.help}>
          Optional — attach an image, video, or audio file. Or keep this project
          text-only.
        </p>
      )}

      {showDropzone && (
        <div
          className={styles.dropzoneWrap}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (pickerDisabled) return;
            handlePick(event.dataTransfer.files?.[0]);
          }}
        >
          {/*
            Native <label> association (not display:none + .click()) so iOS
            Safari opens the file picker reliably from the user gesture.
          */}
          <label
            className={`${styles.dropzone}${
              pickerDisabled ? ` ${styles.disabled}` : ''
            }`}
          >
            <p className={styles.dropzoneTitle}>
              Drop a file here or{' '}
              <span className={styles.browseLink}>browse</span>
            </p>
            <p className={styles.dropzoneMeta}>
              One file · image, video or audio · up to 10 GB
            </p>
            <input
              ref={inputRef}
              className={styles.fileInput}
              type="file"
              accept={ACCEPT}
              disabled={pickerDisabled}
              onChange={(event) => handlePick(event.target.files?.[0])}
            />
          </label>
        </div>
      )}

      {showFileRow && (
        <div className={styles.fileRow}>
          <div
            className={`${styles.fileThumb}${
              rowMediaKind === 'video' ? ` ${styles.fileThumbVideo}` : ''
            }${rowMediaKind === 'audio' ? ` ${styles.fileThumbAudio}` : ''}`}
          >
            {renderThumb()}
          </div>
          <div className={styles.fileDetails}>
            <div className={styles.fileName}>
              {showLiveFileRow ? liveFileName : file?.name || 'Uploading…'}
            </div>
            <div className={styles.fileMetaLine}>
              {showLiveFileRow
                ? liveFileMeta
                : `${file ? formatBytes(file.size) : ''}${
                    mediaKind ? ` · ${mediaKind}` : ''
                  }${fileMetaDuration}${
                    uploading
                      ? ` · uploading${progress ? ` ${progress}%` : ''}`
                      : uploaded
                      ? ' · uploaded'
                      : ''
                  }`}
            </div>
            {uploading && (
              <div className={styles.progressWrap}>
                <div
                  className={styles.progressBar}
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
          {!uploading && (
            <div className={styles.fileActions}>
              <label className={styles.replaceLink}>
                Replace file
                <input
                  ref={replaceInputRef}
                  className={styles.fileInput}
                  type="file"
                  accept={ACCEPT}
                  disabled={pickerDisabled}
                  onChange={(event) => handlePick(event.target.files?.[0])}
                />
              </label>
              {showLiveFileRow ? (
                <button
                  type="button"
                  className={styles.removeLink}
                  onClick={handleRemoveMedia}
                  disabled={disabled}
                >
                  Remove media (make text only)
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.removeLink}
                  onClick={handleClearNewMedia}
                  disabled={disabled}
                >
                  Remove file
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showLiveFileRow && (
        <p className={styles.fileRowHelp}>
          Replacing the file regenerates the thumbnail below.
        </p>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {hasMediaForThumbnail && (
        <div className={styles.thumbnailSection}>
          <div className={styles.heading}>Thumbnail</div>
          <p className={styles.help}>
            {effectiveMediaType === 'video'
              ? "We'll take a frame from the middle of your video. Prefer something else? Upload an image."
              : effectiveMediaType === 'audio'
              ? 'Audio gets a generated card. Add cover art to stand out in client searches.'
              : 'How this project appears on your portfolio and in client searches.'}
          </p>

          {/* Reserved for a future film-strip frame picker (ticket 4 follow-up). */}
          {effectiveMediaType === 'video' ? (
            <div className={styles.filmStripSlot} aria-hidden />
          ) : null}

          <div className={styles.thumbnailLayout}>
            <ProjectThumbnailCard
              imageUrl={previewImageUrl}
              title={resolvedTitle}
              description={projectDescription || project?.description || ''}
              firstRole={firstRole}
              cropSource={thumbnailSource}
              loading={showThumbSkeleton}
              square={showSquareCoverPreview}
              width={
                showSquareCoverPreview
                  ? Math.round(PROJECT_CARD_PREVIEW_WIDTH * 0.75)
                  : PROJECT_CARD_PREVIEW_WIDTH
              }
              showPlayBadge={effectiveMediaType === 'video'}
              frameLabel={videoFrameLabel}
              badgeLabel={cardBadgeLabel}
            />

            <div className={styles.thumbnailActions}>
              {effectiveMediaType === 'video' && thumbnailSource === 'auto' ? (
                <>
                  <button
                    type="button"
                    className={styles.adjustButton}
                    onClick={() => openCoverCropper()}
                    disabled={disabled || uploading || generatingThumb}
                  >
                    Upload an image instead
                  </button>
                  <p className={styles.adjustHelp}>
                    An uploaded image replaces the frame and goes through the
                    same adjust step as any photo.
                  </p>
                </>
              ) : effectiveMediaType === 'audio' &&
                thumbnailSource === 'auto' ? (
                <>
                  <button
                    type="button"
                    className={styles.adjustButton}
                    onClick={() => openCoverCropper()}
                    disabled={disabled || uploading || generatingThumb}
                  >
                    Add cover art
                  </button>
                  <p className={styles.adjustHelp}>
                    Cover art goes through the same adjust step as any image,
                    then replaces the generated card.
                  </p>
                </>
              ) : (
                <>
                  <div className={styles.thumbnailActionRow}>
                    <button
                      type="button"
                      className={styles.adjustButton}
                      onClick={openCoverCropperForCurrentMedia}
                      disabled={disabled || uploading || generatingThumb}
                    >
                      Adjust thumbnail
                    </button>
                    {showAdjustResetActions && (
                      <button
                        type="button"
                        className={
                          effectiveMediaType === 'audio'
                            ? styles.removeLink
                            : styles.resetLink
                        }
                        onClick={handleResetToAutomatic}
                        disabled={
                          disabled ||
                          uploading ||
                          generatingThumb ||
                          thumbnailSource === 'auto'
                        }
                      >
                        {effectiveMediaType === 'audio'
                          ? 'Remove thumbnail'
                          : 'Reset to automatic'}
                      </button>
                    )}
                  </div>
                  <p className={styles.adjustHelp}>
                    The badge says whether the crop is automatic or custom.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <AdjustThumbnailModal
        show={showCoverCropper}
        aspectRatio={coverAspectRatio}
        allowAspectChoice={allowCoverAspectChoice}
        resetRemovesThumbnail={
          effectiveMediaType === 'audio' && thumbnailSource === 'custom'
        }
        initialFile={cropperInitialFile}
        initialSrc={cropperInitialSrc}
        initialCrop={cropperInitialCrop}
        projectTitle={resolvedTitle}
        projectDescription={projectDescription || project?.description || ''}
        firstRole={firstRole}
        onCancel={closeCoverCropper}
        onSave={handleCoverSave}
      />
    </div>
  );
};

ProjectMediaPanel.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  project: PropTypes.object,
  disabled: PropTypes.bool,
  processing: PropTypes.bool,
  projectTitle: PropTypes.string,
  projectDescription: PropTypes.string,
  firstRole: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ProjectMediaPanel.defaultProps = {
  project: null,
  disabled: false,
  processing: false,
  projectTitle: '',
  projectDescription: '',
  firstRole: '',
};

export default ProjectMediaPanel;
